import type { AppDispatch } from '../index';
import { api } from '../api/baseApi';
import { inventoryApi } from '../api/inventoryApi';
import { categoryApi } from '../api/categoryApi';
import { subcategoryApi } from '../api/subcategoryApi';
import { customerApi } from '../api/customerApi';
import { userApi } from '../api/userApi';
import { cashDrawerApi } from '../api/cashDrawerApi';
import { paymentApi } from '../api/paymentApi';
import { storeApi } from '../api/storeApi';
import { outletApi } from '../api/outletApi';
import { userAssignmentApi } from '../api/userAssignmentApi';
import { subscriptionApi } from '../api/subscriptionApi';
import { stockTransferApi } from '../api/stockTransferApi';
import { multiOutletStockApi } from '../api/multiOutletStockApi';
import { unitApi } from '../api/unitApi';
import { bankAccountApi } from '../api/bankAccountApi';

/**
 * Invalidate all RTK Query caches to trigger refetch with new context.
 * Uses invalidateTags instead of resetApiState for smoother transitions -
 * data stays visible while new data is being fetched in background.
 */
export const invalidateAllApiCaches = (dispatch: AppDispatch): void => {
  console.log('[resetApiCache] Invalidating all API caches for store/outlet switch...');

  // Invalidate tags - this triggers refetch while keeping old data visible
  // until new data arrives (smoother UX than resetApiState)

  // Base API tags
  dispatch(api.util.invalidateTags([
    'Auth', 'User', 'Category', 'Subcategory', 'Item', 'ItemStock', 'ItemUnit',
    'Customer', 'Transaction', 'TransactionItem', 'TransactionStats',
    'Payment', 'Expense', 'ExpenseCategory', 'ExpenseSubcategory', 'Budget', 'ExpenseStats',
    'Settings', 'Report', 'AuditLog', 'Audit', 'AuditSummary', 'AuditActions', 'EntityTypes',
    'OutletCash', 'OutletCashTransaction', 'OutletCashAnalytics', 'CashDrawer',
    'InventoryStats', 'Store', 'Outlet', 'StockTransfer', 'SalesAnalytics', 'DebitCardAccount', 'QrisCode'
  ]));

  // Inventory API
  dispatch(inventoryApi.util.invalidateTags([
    'Item', 'Category', 'Subcategory', 'ItemStock', 'InventoryStats', 'ItemUnit', 'StockHistory'
  ]));

  // Category & Subcategory APIs
  dispatch(categoryApi.util.invalidateTags(['Category']));
  dispatch(subcategoryApi.util.invalidateTags(['Subcategory']));

  // Customer API
  dispatch(customerApi.util.invalidateTags(['Customer', 'CustomerStats']));

  // User API
  dispatch(userApi.util.invalidateTags(['User', 'UserStats', 'Permissions', 'UserActivity']));

  // Cash Drawer API
  dispatch(cashDrawerApi.util.invalidateTags(['CashDrawer', 'CashTransaction']));

  // Payment API
  dispatch(paymentApi.util.invalidateTags(['PaymentSettings']));

  // Store & Outlet APIs - Don't invalidate these as they're not store-specific
  // dispatch(storeApi.util.invalidateTags(['Store']));
  // dispatch(outletApi.util.invalidateTags(['Outlet']));

  // User Assignment API
  dispatch(userAssignmentApi.util.invalidateTags(['UserStore', 'UserOutlet']));

  // Subscription API - Don't invalidate, not outlet-specific
  // dispatch(subscriptionApi.util.invalidateTags(['Subscription', 'Plan', 'Invoice']));

  // Stock Transfer API
  dispatch(stockTransferApi.util.invalidateTags(['StockTransfer']));

  // Multi-Outlet Stock API
  dispatch(multiOutletStockApi.util.invalidateTags(['MultiOutletStock']));

  // Unit API
  dispatch(unitApi.util.invalidateTags(['Unit']));

  // Bank Account API
  dispatch(bankAccountApi.util.invalidateTags(['BankAccounts']));

  console.log('[resetApiCache] All API caches invalidated - refetching in background');
};

/**
 * Hard reset all RTK Query API caches.
 * Use this only when you need to completely clear all cached data.
 * WARNING: This causes UI "glitch" as data disappears before new data loads.
 */
export const resetAllApiCaches = (dispatch: AppDispatch): void => {
  console.log('[resetApiCache] Hard resetting all API caches...');

  dispatch(api.util.resetApiState());
  dispatch(inventoryApi.util.resetApiState());
  dispatch(categoryApi.util.resetApiState());
  dispatch(subcategoryApi.util.resetApiState());
  dispatch(customerApi.util.resetApiState());
  dispatch(userApi.util.resetApiState());
  dispatch(cashDrawerApi.util.resetApiState());
  dispatch(paymentApi.util.resetApiState());
  dispatch(storeApi.util.resetApiState());
  dispatch(outletApi.util.resetApiState());
  dispatch(userAssignmentApi.util.resetApiState());
  dispatch(subscriptionApi.util.resetApiState());
  dispatch(stockTransferApi.util.resetApiState());
  dispatch(multiOutletStockApi.util.resetApiState());
  dispatch(unitApi.util.resetApiState());
  dispatch(bankAccountApi.util.resetApiState());

  console.log('[resetApiCache] All API caches reset');
};

/**
 * Clear all application data on logout.
 * This includes:
 * - All RTK Query API caches
 * - All localStorage items related to the app
 * - Redux state will be reset by individual slice reducers
 */
export const clearAllAppData = (dispatch: AppDispatch): void => {
  console.log('[resetApiCache] Clearing all application data for logout...');

  // 1. Reset all RTK Query API caches
  resetAllApiCaches(dispatch);

  // 2. Clear all localStorage items
  const keysToRemove = [
    'token',
    'refreshToken',
    'selectedStoreId',
    'selectedStoreName',
    'selectedOutletId',
    'selectedOutletName',
    'selectedOutletDrawerId',
    'selectedOutletDrawerName',
    'reportFilters',
    'i18nextLng',
    // Add any other app-specific localStorage keys here
  ];

  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`Failed to remove localStorage key: ${key}`, e);
    }
  });

  // 3. Clear sessionStorage as well
  try {
    sessionStorage.clear();
  } catch (e) {
    console.warn('Failed to clear sessionStorage', e);
  }

  console.log('[resetApiCache] All application data cleared');
};
